import { useQueryClient } from '@tanstack/react-query'
import { Alert, Flex, Form, InputNumber, Modal, Spin } from 'antd'
import dayjs from 'dayjs'
import { useEffect } from 'react'

import {
  useTreatmentServiceGetHivAnamnesisCaseEndpointKey,
  useTreatmentServiceGetHivAnamnesisPrefillEndpoint,
  useTreatmentServiceRecordHivAnamnesisAnnualUpdateEndpoint
} from 'src/api/queries'
import {
  AnnualUpdateOtherFields,
  ApiError,
  Gender,
  HivAnamnesisCaseResponse,
  HivAnamnesisForm0581E,
  HivAnamnesisForm0581E_ClinicalSection,
  HivAnamnesisFormSection
} from 'src/api/requests'
import { message, Text } from 'src/components'
import { PersonDemographicsFields } from 'src/features/persons/components/PersonDemographics'
import { useScrollToFirstError, ValidationError } from 'src/hooks'
import { useLocales } from 'src/locales'
import { formatDates, formErrorHandler, parseDates } from 'src/utils'

import { HivAnamnesisFormProvider } from '../context/HivAnamnesisFormContext'
import { cleanClinical, mergeClinical } from '../utils/payload'

import { FieldLabel } from './FieldLabel'
import { HivFormItem } from './HivFormItem'
import { HivInstitutionSelect } from './HivInstitutionSelect'
import { PrefillWarning } from './PrefillWarning'
import { ClinicalFields } from './sections/ClinicalFields'
import { WomanQuestion } from './sections/FamilyCard'

// Same range as the API: from 2000 to the current year.
const MIN_YEAR = 2000

// The ** fields outside section F (3.1, 12 / 12.1, 13, 47.3) are updated yearly for ECDC too.
const PREFILL_SECTIONS = [
  HivAnamnesisFormSection.CLINICAL,
  HivAnamnesisFormSection.INSTITUTIONS,
  HivAnamnesisFormSection.PATIENT,
  HivAnamnesisFormSection.FAMILY
]

const DEMOGRAPHICS_NUMBERS: Record<string, string> = {
  vitalStatus: '12',
  deathDate: '12',
  deathCause: '12.1',
  leftCountry: '13',
  leftCountryDate: '13',
  leftCountryType: '13'
}

/**
 * The stored ** fields outside section F, with what the system has now (the patient profile, the ARV
 * institution, pregnancies) where it has something.
 */
const toOtherFields = (
  stored?: HivAnamnesisForm0581E,
  prefilled?: HivAnamnesisForm0581E
): AnnualUpdateOtherFields => {
  const demographics = {
    ...stored?.patient?.demographics,
    ...prefilled?.patient?.demographics
  }

  return {
    arvInstitutionId:
      prefilled?.institutions?.arvInstitutionId ??
      stored?.institutions?.arvInstitutionId,
    vitalStatus: demographics.vitalStatus,
    deathDate: demographics.deathDate,
    deathCause: demographics.deathCause,
    leftCountry: demographics.leftCountry,
    leftCountryDate: demographics.leftCountryDate,
    leftCountryType: demographics.leftCountryType,
    pregnantAfterDiagnosis: prefilled?.family?.pregnantAfterDiagnosis?.answer
      ? prefilled.family.pregnantAfterDiagnosis
      : stored?.family?.pregnantAfterDiagnosis
  }
}

type Props = {
  hivAnamnesisCase: HivAnamnesisCaseResponse
  onClose: () => void
}

/**
 * The yearly ECDC update (N2): the stored section F refreshed with what the system has now
 * (`sections=clinical`), checked by the doctor and saved as the snapshot of the year. Recording a year again replaces its snapshot.
 */
export const AnnualUpdateModal = ({ hivAnamnesisCase, onClose }: Props) => {
  const { t } = useLocales()
  const queryClient = useQueryClient()
  const [form] = Form.useForm()
  const { onScrollToError } = useScrollToFirstError(form)

  const currentYear = dayjs().year()

  const {
    data: prefill,
    isLoading,
    isError: isPrefillError
  } = useTreatmentServiceGetHivAnamnesisPrefillEndpoint(
    {
      personId: String(hivAnamnesisCase.personId),
      sections: PREFILL_SECTIONS
    },
    [hivAnamnesisCase.personId, ...PREFILL_SECTIONS],
    { retry: false, gcTime: 0 }
  )

  // Without the system data (the prefill failed) the stored values are still shown.
  useEffect(() => {
    if (isLoading || (!prefill?.form && !isPrefillError)) return
    form.setFieldsValue({
      year: currentYear,
      clinical: parseDates(
        mergeClinical(hivAnamnesisCase.form?.clinical, prefill?.form?.clinical)
      ),
      other: parseDates(toOtherFields(hivAnamnesisCase.form, prefill?.form))
    })
  }, [prefill, isLoading, isPrefillError])

  const { mutate, isPending } =
    useTreatmentServiceRecordHivAnamnesisAnnualUpdateEndpoint({
      onError: (err: ApiError) =>
        form.setFields(formErrorHandler(err.body?.errors)),
      onSuccess: (response) => {
        message.success(t('hiv_anamnesis.annual_update.success'))
        queryClient.setQueryData(
          [
            useTreatmentServiceGetHivAnamnesisCaseEndpointKey,
            hivAnamnesisCase.personId
          ],
          response
        )
        onClose()
      }
    })

  const onSubmit = async () => {
    try {
      await form.validateFields()
    } catch (error) {
      onScrollToError(error as ValidationError)

      return
    }

    const values = form.getFieldsValue(true)
    const other = formatDates(values.other ?? {}) as AnnualUpdateOtherFields
    mutate({
      id: String(hivAnamnesisCase.id),
      requestBody: {
        // The version the page loaded: an update over someone else's newer changes is rejected (409).
        version: hivAnamnesisCase.version,
        other: {
          ...other,
          // 47.3 applies only to women.
          pregnantAfterDiagnosis: isWoman ? other.pregnantAfterDiagnosis : null
        },
        year: values.year,
        clinical: cleanClinical(
          formatDates(
            values.clinical ?? {}
          ) as HivAnamnesisForm0581E_ClinicalSection
        )
      }
    })
  }

  const isWoman = hivAnamnesisCase.form?.patient?.gender === Gender.FEMALE

  const recordedYears = new Set(
    hivAnamnesisCase.form?.annualUpdates?.map((x) => x.year) ?? []
  )
  const year = Form.useWatch('year', form)

  return (
    <Modal
      open
      width={1100}
      title={t('hiv_anamnesis.annual_update.title')}
      okText={t('hiv_anamnesis.annual_update.save')}
      cancelText={t('common.cancel')}
      onOk={() => void onSubmit()}
      onCancel={onClose}
      okButtonProps={{ loading: isPending, disabled: isLoading }}
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
    >
      <Spin spinning={isLoading}>
        <HivAnamnesisFormProvider
          sources={prefill?.sources}
          userHasAccessToPersonalData={
            prefill?.userHasAccessToPersonalData ?? true
          }
        >
          <Form
            layout="vertical"
            requiredMark={false}
            form={form}
            disabled={false}
          >
            <Flex vertical gap={16}>
              <Alert
                type="info"
                showIcon
                message={t('hiv_anamnesis.annual_update.hint')}
              />

              <PrefillWarning
                isError={isPrefillError}
                failedSections={prefill?.failedSections}
              />

              <Form.Item
                name="year"
                label={t('hiv_anamnesis.annual_update.year')}
                rules={[{ required: true }]}
                extra={
                  recordedYears.has(year)
                    ? t('hiv_anamnesis.annual_update.replaces_year')
                    : undefined
                }
              >
                <InputNumber min={MIN_YEAR} max={currentYear} precision={0} />
              </Form.Item>

              <Text size="xl">
                {t('hiv_anamnesis.annual_update.other_fields')}
              </Text>

              <HivFormItem
                name={['other', 'arvInstitutionId']}
                number="3.1"
                label={t('hiv_anamnesis.institutions.arv_institution')}
              >
                <HivInstitutionSelect />
              </HivFormItem>

              <PersonDemographicsFields
                namePrefix={['other']}
                onlyVitalAndLeftCountry
                renderLabel={(field, label) => (
                  <FieldLabel
                    number={DEMOGRAPHICS_NUMBERS[field]}
                    label={label}
                    field={`patient.demographics.${field}`}
                  />
                )}
              />

              {isWoman && (
                <WomanQuestion
                  path={['other', 'pregnantAfterDiagnosis']}
                  field="pregnantAfterDiagnosis"
                  number="47.3"
                />
              )}

              <Text size="xl">{t('hiv_anamnesis.clinical.title')}</Text>
              <ClinicalFields
                prefix={['clinical']}
                diagnosisIcd10Id={
                  hivAnamnesisCase.form?.testing?.diagnosis?.icd10Id
                }
              />
            </Flex>
          </Form>
        </HivAnamnesisFormProvider>
      </Spin>
    </Modal>
  )
}
